import { FeatherIcon } from '../../components/FeatherIcon'
import { useStats, useStatsT } from './statsContext'
import { Delta, Empty, ErrorLine, ListBlock, Loading, Pills, TableHead } from './StatsParts'
import { StatsChart } from './StatsChart'
import {
  campaignsQuery,
  countryName,
  countryOthers,
  dig,
  duration,
  flag,
  formatDate,
  int,
  listKeyOf,
  money,
  offerRow,
  offsetLoader,
  PAGE_SIZE,
  PILLS,
  withRange,
  type Raw,
} from './statisticsData'
import { useStat, useStatList } from './statsStore'

export function VisitorsMain() {
  const { t, intl } = useStatsT()
  const { store, range, pills, setPill, countriesAll, showAllCountries } = useStats()
  const by = pills.visitors
  const chart = useStat(store, 'users/me/profile/stats', withRange(range, { by, 'filter[]': 'chart' }))
  const totals = useStat(store, 'users/me/profile/stats', withRange(range, { limit: 10, by: 'total' }))
  const countries = useStat(store, 'users/me/profile/stats', withRange(range, { limit: countriesAll ? 0 : 3, by: 'total', 'filter[]': 'topCountries' }))

  let chartBody
  if (chart.status === 'loading') chartBody = <Loading />
  else if (chart.status === 'error') chartBody = <ErrorLine message={chart.error} />
  else {
    const d = chart.data ?? {}
    chartBody = (
      <>
        <div className="ofx-chart-head">
          <strong>{t('countVisitors', { n: dig(d, 'total').current ?? 0 })}</strong>
          <Delta value={dig(d, 'total').delta} />
        </div>
        <StatsChart
          main={{ points: dig(d, 'chart').visitors, kind: 'count', label: t('l.visitors') }}
          sub={{ points: dig(d, 'chart').duration, kind: 'duration', label: t('l.viewDuration') }}
        />
      </>
    )
  }

  const reachHead = (first: string) => (
    <TableHead cols="reach">
      <span>{first}</span>
      <span className="num">{t('l.guests')}</span>
      <span className="num">{t('l.users')}</span>
      <span className="num">{t('l.total')}</span>
    </TableHead>
  )

  let table
  if (totals.status === 'loading') table = <Loading />
  else if (totals.status === 'error') table = <ErrorLine message={totals.error} />
  else {
    const v = dig(totals.data, 'topCountries', 'totals')
    const d = dig(totals.data, 'topDurationUsers', 'totals')
    table = (
      <>
        {reachHead(t('l.stat'))}
        <div className="ofx-row">
          <div className="ofx-row-grid ofx-cols-reach">
            <strong>{t('l.profileVisitors')}</strong>
            <span className="num">{int(v.guests, false)}</span>
            <span className="num">{int(v.users, false)}</span>
            <strong className="num">{int(v.total)}</strong>
          </div>
        </div>
        <div className="ofx-row">
          <div className="ofx-row-grid ofx-cols-reach">
            <strong>{t('l.viewDuration')}</strong>
            <span className="num">{duration(d.guests)}</span>
            <span className="num">{duration(d.users)}</span>
            <strong className="num">{duration(d.total)}</strong>
          </div>
        </div>
      </>
    )
  }

  let countryBody
  if (countries.status === 'loading') countryBody = <Loading />
  else if (countries.status === 'error') countryBody = <ErrorLine message={countries.error} />
  else {
    const tc = dig(countries.data, 'topCountries')
    const rows = Array.isArray(tc.rows) ? (tc.rows as Raw[]) : []
    const others = countryOthers(tc)
    countryBody = (
      <>
        <div className="ofx-subhead">{t('topCountries')}</div>
        {reachHead(t('l.country'))}
        {rows.map((r, i) => {
          const views = dig(r, 'viewsCount')
          const name = `${flag(r.countryCode)} ${countryName(r.countryCode, String(r.countryName || r.countryCode || '—'), intl)}`.trim()
          return (
            <div key={String(r.countryCode ?? i)} className="ofx-row">
              <div className="ofx-row-grid ofx-cols-reach">
                <strong>{name}</strong>
                <span className="num">{int(views.guests)}</span>
                <span className="num">{int(views.users)}</span>
                <strong className="num">{int(views.total)}</strong>
              </div>
            </div>
          )
        })}
        {!countriesAll && (others[2] ?? 0) > 0 && (
          <div className="ofx-row">
            <div className="ofx-row-grid ofx-cols-reach">
              <strong>{t('others')}</strong>
              <span className="num">{int(others[0])}</span>
              <span className="num">{int(others[1])}</span>
              <strong className="num">{int(others[2])}</strong>
            </div>
          </div>
        )}
        {!countriesAll && Boolean(tc.hasMore) && (
          <div className="ofx-view-all">
            <button type="button" className="ofx-link-btn" onClick={showAllCountries}>
              {t('viewAll')}
            </button>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <Pills items={PILLS.visitors} active={by} onPick={(id) => setPill('visitors', id)} label={t('l.profileVisitors')} />
      <div className="ofx-block">{chartBody}</div>
      <div className="ofx-block ofx-list">{table}</div>
      <div className="ofx-block ofx-list">{countryBody}</div>
    </>
  )
}

/** Promotions and trial links: claims over offers, then the offers with their claims. */
export function OfferMain({ chartPath, listPath, empty }: { chartPath: string; listPath: string; empty: string }) {
  const { t, intl } = useStatsT()
  const { store, range } = useStats()
  const chart = useStat(store, chartPath, withRange(range, { stats: '1' }))
  const listKey = listKeyOf(`offers:${listPath}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, listPath, withRange(range, { limit: PAGE_SIZE }), 'items'))
  if (chart.status === 'loading') return <Loading />
  if (chart.status === 'error') return <ErrorLine message={chart.error} />
  const d = chart.data ?? {}
  if (!(Number(dig(d, 'claims').total) > 0) && !(Number(dig(d, 'offers').total) > 0) && list.loaded && !list.rows.length) return <Empty text={empty} />
  return (
    <>
      <div className="ofx-block">
        <div className="ofx-chart-head">
          <strong>{t('countClaims', { n: int(dig(d, 'claims').total, false) })}</strong>
          <Delta value={dig(d, 'claims').delta} />
        </div>
        <StatsChart main={{ points: dig(d, 'claims').chart, kind: 'count', label: t('l.claims') }} sub={{ points: dig(d, 'offers').chart, kind: 'count', label: t('l.offers') }} />
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={empty}
          header={
            <TableHead cols="links">
              <span>{t('l.startDate')}</span>
              <span>{t('l.endDate')}</span>
              <span className="num">{t('l.price')}</span>
              <span className="num">{t('l.claims')}</span>
            </TableHead>
          }
        >
          {list.rows.map((item, i) => {
            const o = offerRow(item)
            return (
              <div key={String(item.id ?? i)} className="ofx-row">
                {o.title && (
                  <div className="ofx-row-title">
                    <FeatherIcon name={listPath === 'promotions' ? 'percent' : 'gift'} size={15} />
                    {o.title}
                  </div>
                )}
                <div className="ofx-row-grid ofx-cols-links">
                  <span>{o.created ? formatDate(o.created, intl) : '—'}</span>
                  <span>{o.finished ? formatDate(o.finished, intl) : '-'}</span>
                  <span className="num">{o.price !== null && o.price !== undefined ? (Number(o.price) > 0 ? money(o.price) : t('free')) : '—'}</span>
                  <strong className="num ofx-accent">{int(o.claims)}</strong>
                </div>
              </div>
            )
          })}
        </ListBlock>
      </div>
    </>
  )
}

export function TrackingMain() {
  const { t, intl } = useStatsT()
  const { store, range } = useStats()
  const chart = useStat(store, 'campaigns/chart', withRange(range, { 'filter[clicks]': 'clicks', 'filter[claims]': 'claims' }))
  const listKey = listKeyOf('campaigns', range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'campaigns', campaignsQuery(range)))
  const d = chart.data ?? {}
  return (
    <>
      <div className="ofx-block">
        {chart.status === 'loading' ? (
          <Loading />
        ) : chart.status === 'error' ? (
          <ErrorLine message={chart.error} />
        ) : (
          <>
            <div className="ofx-chart-head">
              <strong>{t('countSubscribers', { n: int(d.total, false) })}</strong>
              <Delta value={d.delta} />
            </div>
            <StatsChart main={{ points: d.claims, kind: 'count', label: t('l.subscribers') }} sub={{ points: d.clicks, kind: 'count', label: t('l.clicks') }} />
          </>
        )}
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.trackingLinks')}
          header={
            <TableHead cols="links">
              <span>{t('l.startDate')}</span>
              <span>{t('l.endDate')}</span>
              <span className="num">{t('l.clicks')}</span>
              <span className="num">{t('l.subscribers')}</span>
            </TableHead>
          }
        >
          {list.rows.map((c, i) => (
            <div key={String(c.id ?? i)} className={`ofx-row ${c.isDeleted ? 'is-deleted' : ''}`}>
              <div className="ofx-row-title">
                <FeatherIcon name="link" size={15} />
                {String(c.campaignName || `#${c.campaignCode ?? c.id}`)}
              </div>
              <div className="ofx-row-grid ofx-cols-links">
                <span>{formatDate(c.createdAt, intl)}</span>
                <span>{c.endDate ? formatDate(c.endDate, intl) : '-'}</span>
                <span className="num">{int(c.countTransitions)}</span>
                <strong className={`num ${Number(c.countSubscribers) > 0 ? 'ofx-accent' : ''}`}>{int(c.countSubscribers)}</strong>
              </div>
            </div>
          ))}
        </ListBlock>
      </div>
    </>
  )
}

export function PromotionsMain() {
  const { t } = useStatsT()
  return <OfferMain chartPath="promotions/chart" listPath="promotions" empty={t('empty.profile')} />
}

export function TrialsMain() {
  const { t } = useStatsT()
  return <OfferMain chartPath="trials/chart" listPath="trials/stats" empty={t('empty.profile')} />
}
